import React from "react";
import { Link } from "react-router-dom";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { IMG } from "@/data/content";
import { useContent } from "@/context/ContentContext";
import { ArrowRight, Sparkles, Heart, TrendingUp, Sun, Droplet, Eye } from "lucide-react";

const ICONS = { Heart, Sparkles, TrendingUp, Sun, Droplet, Eye };

const resolveImg = (v) =>
  v && typeof v === "string" && v.startsWith("http") ? v : IMG[v] || v;

// Points may come from the CMS either as a string ("A, B, C") or an array
const pointsToArray = (raw) => {
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (typeof raw === "string") {
    return raw
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
};

// Renders a title, replacing 'single-quoted phrase' with italic gold
const renderTitle = (title) => {
  if (!title) return null;
  const parts = title.split(/'([^']+)'/g);
  return parts.map((chunk, idx) =>
    idx % 2 === 1 ? <em key={idx}>{chunk}</em> : <span key={idx}>{chunk}</span>
  );
};

const ServicePanel = ({ s }) => (
  <div className="grid gap-12 lg:grid-cols-12 items-start">
    <Reveal className="lg:col-span-7">
      <div className="eyebrow mb-4">{s.eyebrow}</div>
      <h2 className="display text-4xl md:text-5xl text-ivory">{renderTitle(s.title)}</h2>
      <p className="mt-6 text-ivory/70 leading-relaxed">{s.intro}</p>
      <ul className="mt-8 grid sm:grid-cols-2 gap-3">
        {pointsToArray(s.points).map((p, i) => (
          <li key={i} className="flex items-start gap-3 text-ivory/80">
            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-gold shrink-0" />
            <span>{p}</span>
          </li>
        ))}
      </ul>
      <Link to="/contact" className="btn-gold mt-10" data-testid={`service-cta-${s.id}`}>
        Begin with this service <ArrowRight className="h-4 w-4" />
      </Link>
    </Reveal>
    <Reveal delay={120} className="lg:col-span-5">
      <div className="glass p-2 ring-gold">
        <img src={resolveImg(s.image) || IMG.mandala} alt={s.label} className="aspect-[4/5] w-full object-cover" />
      </div>
    </Reveal>
  </div>
);

const Services = () => {
  const { content } = useContent();
  const services = (content.services || []).filter((s) => s && s.id);
  const steps = content.howItWorks || [];
  const benefits = content.benefits || [];
  const sec = content.sections || {};
  const sp = content.servicesPage || {};

  return (
    <div data-testid="services-page">
      <PageHero
        eyebrow={sp.eyebrow || "Our Services"}
        title={renderTitle(sp.title || "Four 'sacred instruments'. One transformation.")}
        subtitle={sp.subtitle || "Each service stands on its own — and each is more powerful when woven into the full Geomantic Kalpataru journey."}
        image={resolveImg(sp.heroImage) || IMG.temple}
      />

      {/* Tabbed services */}
      <section className="section">
        <div className="wrap">
          {services.length > 0 && (
            <Tabs defaultValue={services[0].id} className="w-full">
              <TabsList
                data-testid="services-tabs"
                className="flex h-auto flex-wrap justify-center gap-2 bg-transparent p-0 mb-12"
              >
                {services.map((s) => (
                  <TabsTrigger
                    key={s.id}
                    value={s.id}
                    data-testid={`service-tab-${s.id}`}
                    className="rounded-none border border-gold/25 bg-midnight/40 px-5 py-3 text-[11px] uppercase tracking-[0.22em] text-ivory/70 transition data-[state=active]:bg-gold data-[state=active]:text-midnight data-[state=active]:border-gold hover:text-gold"
                  >
                    {s.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              {services.map((s) => (
                <TabsContent key={s.id} value={s.id} className="mt-0">
                  <ServicePanel s={s} />
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>
      </section>

      {/* Process */}
      <section className="section border-t border-gold/10 bg-midnight-paper/40">
        <div className="wrap">
          <Reveal>
            <div className="eyebrow mb-5">{sp.processEyebrow || sec.howItWorksEyebrow || "The Process"}</div>
            <h2 className="display text-4xl md:text-5xl text-ivory max-w-3xl">
              {sp.processTitle || "Five sacred steps — applied across every service."}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-5">
            {steps.map((step, i) => (
              <Reveal key={(step.n || "") + i} delay={i * 70}>
                <div className="glass lift p-6 h-full">
                  <div className="font-display text-5xl text-gold/40">{step.n}</div>
                  <div className="gold-divider my-4" />
                  <h3 className="font-display text-2xl text-ivory">{step.title}</h3>
                  <p className="mt-3 text-ivory/65 text-sm leading-relaxed">{step.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section">
        <div className="wrap">
          <Reveal>
            <div className="eyebrow mb-5">{sp.benefitsEyebrow || "Unique Benefits"}</div>
            <h2 className="display text-4xl md:text-5xl text-ivory max-w-3xl">
              {sp.benefitsTitle || sec.benefitsTitle || "Six domains that quietly, decisively shift."}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {benefits.map((b, i) => {
              const Icon = ICONS[b.icon] || Sparkles;
              return (
                <Reveal key={b.title + i} delay={i * 60}>
                  <div className="glass lift p-7 h-full">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 bg-gold/5">
                      <Icon className="h-5 w-5 text-gold" strokeWidth={1.4} />
                    </div>
                    <h3 className="mt-6 font-display text-2xl text-ivory">{b.title}</h3>
                    <p className="mt-3 text-ivory/65 leading-relaxed">{b.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section border-t border-gold/10">
        <div className="wrap text-center max-w-3xl">
          <Reveal>
            <div className="eyebrow mb-5">{sp.ctaEyebrow || "Begin"}</div>
            <h2 className="display text-4xl md:text-5xl text-ivory">
              {renderTitle(sp.ctaTitle || "Speak with us. The path begins with a 'conversation'.")}
            </h2>
            <Link to="/contact" className="btn-gold mt-8" data-testid="services-bottom-cta">
              {sp.ctaButton || "Book a Consultation"} <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Services;
